import './CategorizationReviewPage.scss';
import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "../../stores";
import Typography from "@mui/material/Typography";
import {Alert, Box, CircularProgress, Grid, Snackbar} from "@mui/material";
import {CurrencyComponent} from "../../components/currency-component/CurrencyComponent";
import {SingleBarChart} from "../../components/visualization/single-barchart/SingleBarChart";
import {NO_MARGIN} from "../../utils/margin";
import {UNCATEGORIZED_VALUE} from "../../constants";
import {ReviewBreadcrumbs} from "./components/review-breadcrumbs/ReviewBreadcrumbs";
import {CategorizationReviewTableMuiX} from "./components/review-table/review-table-muix/CategorizationReviewTableMuiX";
import {environment} from "../../env";
import {CategorizationDetailModal} from "./CategorizationDetailModal";
import {DefaultAlert} from "./components/review-table/alerts/DefaultAlert";
import {ApprovalProcessingAlert} from "./components/review-table/alerts/ApprovalProcessingAlert";
import {SampleOverview} from "./components/sample-overview/SampleOverview";
import {SelectionSnackbar} from "./components/review-table/selection/SelectionSnackbar";
import {ErrorBoundary} from "react-error-boundary";
import type {KpiBoxedValue} from "../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import {KpiBoxedValues} from "../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import {EXTEND_NAVIGATION_THRESHOLD} from "../../components/visualization/BarChart.tsx";

export const CategorizationReviewPage: React.FC = observer(() => {
    const {categorizationReviewStore, materializedApi, authStore} = useStores();
    const reviewPageController = categorizationReviewStore.reviewPageController;
    const reviewPageFilterController = categorizationReviewStore.reviewPageController.reviewPageFilterController;
    const statusDelegate = categorizationReviewStore.statusDelegate;

    const parentCharts = categorizationReviewStore.reviewPageController.parentCharts;
    const selectionCharts = categorizationReviewStore.reviewPageController.reviewPageFilterController.selectionCharts;
    const keyValues = categorizationReviewStore.reviewPageController.reviewPageStatisticsController.summaryResultKeyValues;

    const [isExtended, setIsExtended] = React.useState<boolean>(false);
    const canExtend = selectionCharts ? selectionCharts.data.length > EXTEND_NAVIGATION_THRESHOLD : false;

    const dataMax = Math.max(selectionCharts?.max || 1, parentCharts?.max || 1);

    const showSampleOverview = categorizationReviewStore.isValidationBag;
    const databag = categorizationReviewStore.bagId;

    useEffect(() => {
        // TODO CAT-1580: This is request is repeated twice, databag initialization is messy!
        // Also see CategorizationReviewPageDataController.onAnyReviewUpdatePipe
        if (showSampleOverview) {
            materializedApi.getMatReviewSampleStatistics(databag)
                .then(resp => categorizationReviewStore.reviewPageController.reviewPageStatisticsController.setSampleStatistics(resp.data));
        } else {
            categorizationReviewStore.reviewPageController.reviewPageStatisticsController.setSampleStatistics(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [databag, showSampleOverview]);

    const kpiValues = React.useMemo<KpiBoxedValue[]>(() => [
        {
            title: 'Categorized',
            subtitle: keyValues
                ? <CurrencyComponent
                    v={keyValues.after.classified_spend}
                    nDigits={3}
                    abbreviation={keyValues.abbreviation}
                    hideAlmostZero={true}
                />
                : <>&nbsp;</>
        }, {
            title: 'Re-categorized',
            subtitle: keyValues
                ? <CurrencyComponent
                    v={keyValues.after.reclassified_spend}
                    nDigits={3}
                    abbreviation={keyValues.abbreviation}
                    hideAlmostZero={true}
                />
                : <>&nbsp;</>
        }, {
            title: 'Uncategorized',
            subtitle: keyValues
                ? <CurrencyComponent
                    v={keyValues.after.unclassified_spend}
                    nDigits={3}
                    abbreviation={keyValues.abbreviation}
                    hideAlmostZero={true}
                />
                : <>&nbsp;</>,
            className: 'value-warning'
        },
    ], [keyValues]);

    const reviewPageIsLoading: boolean = categorizationReviewStore.reviewPageController.reviewPageIsLoading;
    if (reviewPageIsLoading) {
        return (
            <Grid container justifyContent="center" style={{marginTop: '7em'}}>
                <Grid>
                    <CircularProgress size={24}/>
                </Grid>
            </Grid>
        );
    }

    return (
        <div className="categorization-review-page">

            <SelectionSnackbar/>
            <Snackbar
                open={categorizationReviewStore.reviewPageController.reviewPageApprovalController.isSendForApprovalWaiting}
                anchorOrigin={{
                    vertical: 'top', // Ensure it doesn't overlap with the SelectionSnackbar
                    horizontal: 'center',
                }}
                message="Approval request is being sent, please wait ..."
                action={<CircularProgress size={18} color="inherit" style={{margin: '0 1em'}}/>}
            />
            <CategorizationDetailModal isOpen={false} setOpen={() => {
            }}></CategorizationDetailModal>

            {environment.isTestReviewPage && authStore.isMithraStaff && <>
                <Box component="pre" sx={{maxWidth: '1700px'}}>
                    canEditReviewPage={'' + categorizationReviewStore.canEditReviewPage} (viewOnly={'' + categorizationReviewStore.viewOnly},
                    isSendForApprovalWaiting={'' + categorizationReviewStore.reviewPageController.reviewPageApprovalController.isSendForApprovalWaiting},
                    cats[]={'' + categorizationReviewStore.categories.taxonomy_categories?.length})<br/>
                    isReviewLevelStatisticsEmpty={'' + categorizationReviewStore.reviewPageController.reviewPageStatisticsController.isReviewLevelStatisticsEmpty}<br/>
                    reviewStatistics={JSON.stringify(categorizationReviewStore.reviewPageController.reviewPageStatisticsController.reviewStatistics)}<br/>
                    reviewLevelStatistics={JSON.stringify(categorizationReviewStore.reviewPageController.reviewPageStatisticsController.reviewLevelStatistics)}<br/>
                    selectedLevel={JSON.stringify(categorizationReviewStore.reviewPageController.reviewPageFilterController.selectedLevel)}<br/>
                    selectedCategory={JSON.stringify(categorizationReviewStore.reviewPageController.reviewPageFilterController.selectedCategory)}<br/>
                    keyValues={JSON.stringify(keyValues)}<br/>
                </Box>
            </>}

            {showSampleOverview && <SampleOverview/>}

            {!showSampleOverview && <KpiBoxedValues values={kpiValues}/>}

            <ReviewBreadcrumbs previousCategory/>
            <ReviewBreadcrumbs/>

            {selectionCharts &&
                <Grid container>
                    <Grid className="overview-bar-charts" size={12}>
                        {/* The bars of the bar chart */}
                        {selectionCharts.data.slice(0, isExtended ? undefined : EXTEND_NAVIGATION_THRESHOLD).map(changeChartData =>
                            <div key={changeChartData.category}
                                 className={`clickable bar-chart-container l${reviewPageFilterController.selectedLevel + 1}`}
                                 onClick={(e) => {
                                     reviewPageFilterController.selectNextCategoryDown(changeChartData.category, e.target['__data__']?.dataCls ?? '')
                                 }}>
                                <SingleBarChart
                                    key={changeChartData.category}
                                    data={{
                                        mainLabel: changeChartData.label,
                                        values: changeChartData.values,
                                    }}
                                    className={changeChartData.category === UNCATEGORIZED_VALUE ? 'uncat' : ''}
                                    options={{
                                        width: 1300,
                                        height: 45,
                                        margin: NO_MARGIN,
                                        dataMax,
                                        labelHover: true
                                    }}
                                />
                            </div>
                        )}
                    </Grid>
                    <Grid className={'text-center'} alignItems={'center'} size={12}>
                        {canExtend && <Typography
                            variant="h6"
                            className={'clickable'}
                            onClick={() => setIsExtended(!isExtended)}
                            sx={{margin: '.5em'}}>
                            {isExtended ? 'Show less' : 'Show more'}
                        </Typography>}
                    </Grid>
                </Grid>
            }

            {reviewPageController.errorMessage &&
                <Grid container className="review-table-head-alerts-container" justifyContent="center">
                    <Grid>
                        <Alert severity="error">
                            {reviewPageController.errorMessage}
                        </Alert>
                    </Grid>
                </Grid>
            }
            {reviewPageController.reviewPageTableController.invalidSortKeyWarnings &&
                <Grid container className="review-table-head-alerts-container" justifyContent="center">
                    <Grid>
                        <Alert severity="warning">
                            Sorting on {reviewPageController.reviewPageTableController.invalidSortKeyWarnings} does not
                            have any effect until a group is expanded.
                        </Alert>
                    </Grid>
                </Grid>
            }
            <Grid container className="review-table-head-alerts-container" justifyContent="center">
                <>
                    {
                        statusDelegate.isCategorizationApprovalBusy
                            ? <ApprovalProcessingAlert/>
                            : <DefaultAlert/>
                    }
                </>
            </Grid>

            <section className="table-section">
                <ErrorBoundary fallback={<Alert severity="error">Table could not be loaded</Alert>}>
                    <CategorizationReviewTableMuiX/>
                </ErrorBoundary>
            </section>
        </div>
    );
});
